// src/Pendo.tsx
import { useEffect } from 'react';

// Loads the Pendo script if not already loaded
function loadPendoScript(apiKey: string): Promise<void> {
  return new Promise((resolve) => {
    if (document.getElementById('pendo-script')) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = 'pendo-script';
    script.async = true;
    script.src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
    script.onload = () => resolve();
    document.body.appendChild(script);
  });
}

// Call this to initialize Pendo with visitor/account info
export async function initializePendo({
  apiKey,
  visitor = {},
  account = {},
}: {
  apiKey: string;
  visitor?: Record<string, any>;
  account?: Record<string, any>;
}) {
  console.log('Initializing Pendo with API Key:', apiKey);
  await loadPendoScript(apiKey);
  
  // Now that the script is loaded, initialize Pendo
  (window as any).pendo = (window as any).pendo || {};
  
  // Initialize with the API key explicitly included
  (window as any).pendo.initialize({
    visitor,
    account,
    apiKey
  });
}

// Optional: React hook to auto-load Pendo script on mount
export function usePendo(apiKey: string) {
  useEffect(() => {
    if (!apiKey) return;
    
    const loadScript = async () => {
      await loadPendoScript(apiKey);
    };
    
    loadScript();
  }, [apiKey]);
}

// Helper function to manually validate Pendo environment
export function validatePendoEnvironment() {
  if ((window as any).pendo && (window as any).pendo.validateEnvironment) {
    console.log('Validating Pendo environment...');
    (window as any).pendo.validateEnvironment();
    return true;
  } else {
    console.error('Pendo not properly initialized. Cannot validate environment.');
    return false;
  }
}

// Usage example (not included in output):
// initializePendo({
//   apiKey: 'your-api-key',
//   visitor: { id: 'visitor-id' },
//   account: { id: 'account-id' },
// }); 